<template>
  <Header
    navbar-type="goBack"
    show-help-btn
    @clicked="$router.back"
    :screen-title="t('WITHDRAWAL')"
    hide-help-btn-text
    :help-icon="useAsset('img/credit-line/help-question-mark.svg')"
  />
  <AntiMoneyLaunderingSurvey
    v-if="
      amlSettings && amlSettings.questions && amlSettings.questions.length > 0
    "
    @on-continue-click="saveSurvey"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AntiMoneyLaunderingSurvey from "@/apps/credit-line/components/Withdrawal/AntiMoneyLaunderingSurvey.vue";
import { getCountry } from "@/apps/credit-line/helpers/translations.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import Header from "@/shared-components/consumer/Header.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { useStore } from "vuex";
import { Service as ApiService } from "@/apps/credit-line/services/network.service";
import { useSecureWithdrawal } from "@/apps/credit-line/hooks/useSecureWithdrawal";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { AMLSettings } from "@/apps/credit-line/helpers/aml-settings.helper";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const queryParams = route.query;
const apiService = new ApiService();

const countryCl = ref<CL_COUNTRY | undefined>(undefined);
const amlSettings = ref<AMLSettings>();

provide("amlSettings", amlSettings);

const { summary } = useCreditLineSummary();
const userId = computed(() => summary.value?.userId);
const creditLineId = computed(() => summary.value?.creditLineId);
const withdrawalEstimation = computed(() => {
  return store.getters["creditLine/getWithdrawalEstimate"];
});
const bankTransfer = computed(
  () =>
    store.getters["creditLine/getSelectedWithdrawMethod"] ===
    WithdrawMethod.ONLINE
);

const { alertMessage, secureWithdrawal } = useSecureWithdrawal({
  bankTransfer: bankTransfer.value,
  t,
});

const saveSurvey = async (data) => {
  data.userId = userId.value;
  data.deviceTag = AuthenticationHelper.getDeviceTag();
  data.creditLineId = creditLineId.value;
  try {
    const res = await apiService.SaveAmlSurvey(data);
    if (res.data?.success) {
      // Only proceed with withdrawal if there's no blocking answer
      if (!data.hasBlockingAnswer) {
        secureWithdrawal({
          withdrawing: withdrawalEstimation.value?.amount,
          withdrawalEstimation: withdrawalEstimation.value,
          onError: () => {
            router.replace({
              name: "CreditLineApiErrorDisplay",
              query: {
                title: t("ERROR_SOMETHING_GOES_WRONG"),
                description: alertMessage.value,
                returnHomeOnTryAgain: "true",
              },
            });
          },
        });
      }
    }
  } catch (err) {
    ErrorHandler.handleNetworkErrorAndLog(err);
    void router.replace({
      name: "CreditLineApiErrorDisplay",
      query: {
        title: t("ERROR_SOMETHING_GOES_WRONG"),
        description: err.response?.data?.message ?? "",
        returnHomeOnTryAgain: "true",
      },
    });
  }
};

const getAMLSettings = async (): Promise<void> => {
  try {
    const cardActivationSettings = await getSettings(
      SettingsCategory.CL_CARDS_PAGE
    );
    amlSettings.value = cardActivationSettings?.amlSurveyConfiguration?.values
      ?.formValuesRules as unknown as AMLSettings | null;
  } catch (error) {
    console.error("Error fetching AML settings:", error);
  }
};

onMounted(async () => {
  await getAMLSettings();

  if (
    queryParams.country &&
    Object.values(CL_COUNTRY).includes(queryParams.country as CL_COUNTRY)
  ) {
    countryCl.value = queryParams.country as CL_COUNTRY;
  } else {
    countryCl.value = await getCountry();
  }

  safeAndroidCall("hideNav");
});
</script>
